(() => {
  const prepareProductImage = (image, source, signal) => new Promise((resolve, reject) => {
    let settled = false, decoding = false;
    const cleanup = () => {
      window.clearTimeout(timer);
      image.removeEventListener('load', loaded);
      image.removeEventListener('error', failed);
      signal.removeEventListener('abort', aborted);
    };
    const finish = error => {
      if (settled) return;
      settled = true;
      cleanup();
      if (error) {
        image.removeAttribute('srcset');
        image.removeAttribute('src');
        reject(error);
      } else resolve(image);
    };
    const loaded = async () => {
      if (settled || decoding) return;
      decoding = true;
      try {
        if (!image.naturalWidth) throw new Error('Image unavailable');
        if (image.decode) await image.decode();
        if (!image.naturalWidth) throw new Error('Image unavailable');
        finish();
      } catch (error) { finish(error); }
    };
    const failed = () => finish(new Error('Image unavailable'));
    const aborted = () => finish(new DOMException('Image request replaced', 'AbortError'));
    const timer = window.setTimeout(() => finish(new Error('Image timed out')), 15000);
    image.addEventListener('load', loaded);
    image.addEventListener('error', failed);
    signal.addEventListener('abort', aborted, { once: true });
    if (signal.aborted) { aborted(); return; }
    image.srcset = source.srcset;
    image.src = source.src;
    if (image.complete) queueMicrotask(() => { if (image.naturalWidth) loaded(); else failed(); });
  });

  const initialize = root => {
    if (root.dataset.refillInitialized) return;
    root.dataset.refillInitialized = 'true';
    const data = JSON.parse(root.querySelector('[data-refill-product-data]').textContent);
    const variants = data.variants.filter(variant => /^\d+$/.test(variant.id) && ['500', '1000', '6000'].includes(variant.quantity));
    const media = root.querySelector('[data-refill-main-media]');
    const returnLink = root.querySelector('[data-refill-return]');
    const initialId = root.dataset.currentVariant;
    let selectedId = initialId;
    let imageVersion = 0;
    let imageController = null;
    let displayedId = media.querySelector('img') ? initialId : '';
    media.classList.add('dy-refill-image-frame');
    const message = (key, selection) => (data.strings[key] || data.strings.image_unavailable || '').split('[[selection]]').join(selection || '');
    const render = (id, updateURL = true) => {
      const variant = variants.find(item => item.id === String(id));
      if (!variant) return;
      const version = ++imageVersion;
      selectedId = variant.id;
      root.querySelectorAll('[data-refill-variant]').forEach(input => { input.checked = input.value === variant.id; });
      root.querySelectorAll('[data-refill-product-title]').forEach(title => { title.textContent = variant.title; });
      root.querySelector('[data-refill-pack-copy]').textContent = data.strings.quantity.replace('[[count]]', new Intl.NumberFormat(data.locale).format(Number(variant.quantity)));
      const returnURL = new URL(returnLink.href);
      returnURL.searchParams.set('color', root.dataset.colorCode);
      returnURL.searchParams.set('quantity', variant.quantity);
      returnURL.searchParams.set('size', variant.beadSize.replace(' mm',''));
      root.querySelectorAll('[data-refill-size-copy]').forEach(node => { node.textContent = variant.beadSize; });
      returnLink.href = returnURL.href;
      if (updateURL) {
        const url = new URL(location.href);
        url.searchParams.set('variant', variant.id);
        history.replaceState(history.state, '', url);
      }
      imageController?.abort();
      const controller = new AbortController();
      imageController = controller;
      const currentImage = media.querySelector('img');
      const clearStatus = () => {
        media.querySelector('.dy-refill-image-notice')?.remove();
        media.removeAttribute('aria-busy');
        media.removeAttribute('data-image-state');
        media.querySelector('img')?.removeAttribute('aria-hidden');
      };
      const notice = (text, retry = false) => {
        media.querySelector('.dy-refill-image-notice')?.remove();
        if (!currentImage) media.replaceChildren();
        currentImage?.setAttribute('aria-hidden', 'true');
        media.dataset.imageState = retry ? 'error' : 'loading';
        const panel = document.createElement('div');
        panel.className = 'dy-refill-image-notice';
        panel.setAttribute('role', 'status');
        panel.setAttribute('aria-live', 'polite');
        const paragraph = document.createElement('p');
        paragraph.textContent = text;
        panel.append(paragraph);
        if (currentImage) {
          const previous = document.createElement('small');
          previous.textContent = message('image_previous');
          panel.append(previous);
        }
        if (retry) {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'dy-refill-image-retry';
          button.textContent = message('image_retry');
          button.addEventListener('click', () => render(variant.id));
          panel.append(button);
        }
        media.append(panel);
      };
      if (displayedId === variant.id && currentImage?.complete && currentImage.naturalWidth) { clearStatus(); return; }
      if (!variant.image) {
        notice(data.strings.image_unavailable);
        media.dataset.imageState = 'error';
        media.removeAttribute('aria-busy');
        return;
      }
      let source;
      try {
        source = new URL(variant.image, location.origin);
        if (source.protocol !== 'https:' || (source.origin !== location.origin && source.hostname !== 'cdn.shopify.com' && !source.hostname.endsWith('.shopifycdn.net'))) throw new Error('Invalid image');
      } catch {
        notice(data.strings.image_unavailable);
        media.dataset.imageState = 'error';
        media.removeAttribute('aria-busy');
        return;
      }
      notice(message('image_loading', variant.alt));
      media.setAttribute('aria-busy', 'true');
      const image = document.createElement('img');
      image.width = image.height = 1120;
      image.alt = variant.alt;
      image.decoding = 'async';
      image.sizes = '(min-width: 900px) 55vw, 100vw';
      prepareProductImage(image, {
        srcset: [360, 720, 1120].map(width => { const url = new URL(source); url.searchParams.set('width', String(width)); return url.href + ' ' + width + 'w'; }).join(', '),
        src: source.href
      }, controller.signal).then(() => {
        if (imageVersion !== version || controller.signal.aborted || !root.isConnected) return;
        media.replaceChildren(image);
        displayedId = variant.id;
        clearStatus();
      }).catch(error => {
        if (imageVersion !== version || controller.signal.aborted || !root.isConnected || error.name === 'AbortError') return;
        media.removeAttribute('aria-busy');
        notice(message('image_error', variant.alt), true);
      });
    };
    root.addEventListener('change', event => {
      if (event.target.matches('[data-refill-variant], [data-variant-select]') && event.target.value !== selectedId) render(event.target.value);
    });
    const restore = () => render(new URL(location.href).searchParams.get('variant') || initialId, false);
    window.addEventListener('popstate', restore);
    const unload = event => {
      if (!event.target.contains(root)) return;
      imageController?.abort();
      window.removeEventListener('popstate', restore);
      document.removeEventListener('shopify:section:unload', unload);
    };
    document.addEventListener('shopify:section:unload', unload);
  };
  const start = () => document.querySelectorAll('[data-refill-product]').forEach(initialize);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
  document.addEventListener('shopify:section:load', start);
})();
